import { FloatingActions } from "@/components/floating-actions/floating-actions";
import { ListStateObservable } from "@/store/pages/ListStateObservable";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import {
  MDSActionMenuDropdown,
  MDSButton,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconKind,
  MDSMenuContentItemKind,
  MDSMenuContentList,
  MDSMenuDropdownSlotName,
  MDSTooltipPlacement,
  MDSIcon,
  MDSPanelResponsiveWrapper,
  MDSSpacer,
} from "@/mds";
import { MDSTooltip } from "@/mds/components/tooltip";
import { getShortcutTooltipConfig } from "@/modules/keyboard-shortcuts/tooltip";
import { getShortcutMenuItemRightAddon } from "@/modules/keyboard-shortcuts/menu";

export interface MultiSelectActionsProps {
  listState: ListStateObservable;
}

export const MultiSelectActions = observer<MultiSelectActionsProps>(({ listState }) => {
  const numberOfSelectedIds = listState.selectedItemIds.length;
  const isOpen = !!numberOfSelectedIds;
  const autoOrganizeTooltipLabel = listState.canAutoOrganize
    ? "Let Mem organize your notes"
    : "Auto Organize is available for notes";

  const RESPONSIVE_THRESHOLD = "575px";

  const baseMoreActions: MDSMenuContentList = [
    ...(listState.canAddToCollection
      ? [
          {
            id: "add-to-collection",
            kind: MDSMenuContentItemKind.Item as const,
            label: "Organize",
            icon: MDSIconKind.Collection,
            rightAddon: getShortcutMenuItemRightAddon({ action: "organize_selected_notes" }),
            onClick: listState.handleAddToCollection,
          },
        ]
      : []),
    ...(listState.canRemoveFromCollection
      ? [
          {
            id: "remove-from-collection",
            kind: MDSMenuContentItemKind.Item as const,
            label: "Remove from collection",
            icon: MDSIconKind.Minus,
            onClick: listState.handleRemoveFromCollection,
          },
        ]
      : []),
    ...(listState.supportsCopyNoteAsMarkdownListAction
      ? [
          {
            id: "copy-as-markdown",
            kind: MDSMenuContentItemKind.Item as const,
            label: "Copy as Markdown",
            icon: MDSIconKind.Text,
            onClick: listState.handleCopyNoteAsMarkdown,
          },
        ]
      : []),
  ];

  const collapsedOverflowActions: MDSMenuContentList = [
    ...(listState.canChatWithSelection
      ? [
          {
            id: "chat",
            kind: MDSMenuContentItemKind.Item as const,
            label: "Chat",
            icon: MDSIconKind.Message,
            rightAddon: getShortcutMenuItemRightAddon({ action: "open_mem_agent" }),
            onClick: listState.handleChatWithSelection,
          },
        ]
      : []),
    ...(listState.canRestoreFromTrash
      ? [
          {
            id: "restore-from-trash",
            kind: MDSMenuContentItemKind.Item as const,
            label: "Restore from trash",
            icon: MDSIconKind.Redo,
            onClick: listState.handleRestore,
          },
        ]
      : []),
    ...(listState.canMoveNoteToTrash
      ? [
          {
            id: "move-to-trash",
            kind: MDSMenuContentItemKind.Item as const,
            label: "Trash",
            icon: MDSIconKind.Trash,
            rightAddon: getShortcutMenuItemRightAddon({ action: "move_notes_to_trash" }),
            onClick: listState.handleDeleteNote,
          },
        ]
      : []),
    ...(listState.canMoveCollectionToTrash
      ? [
          {
            id: "delete-collection",
            kind: MDSMenuContentItemKind.Item as const,
            label: "Delete collection",
            icon: MDSIconKind.Trash,
            onClick: listState.handleDeleteCollections,
          },
        ]
      : []),
  ];

  const smallScreenMoreActions: MDSMenuContentList = [
    ...collapsedOverflowActions,
    ...baseMoreActions,
  ];

  return (
    <FloatingActions isOpen={isOpen}>
      <DeselectButton variant={MDSButtonVariant.EmphasisLight} onClick={listState.clearSelection}>
        <span>{numberOfSelectedIds} selected</span>
        <MDSIcon kind={MDSIconKind.Exit} slot={MDSButtonSlotName.RightIcon} />
      </DeselectButton>
      <Box>
        <MDSSpacer />
        {listState.supportsAutoOrganize && (
          <MDSTooltip
            config={getShortcutTooltipConfig({
              action: "auto_organize_selected_notes",
              label: autoOrganizeTooltipLabel,
              placement: MDSTooltipPlacement.Top,
            })}
          >
            <MDSButton
              variant={MDSButtonVariant.Chrome}
              onClick={listState.handleAutoOrganize}
              isDisabled={!listState.canAutoOrganize}
            >
              <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.AutoOrganize} />
              Auto Organize
            </MDSButton>
          </MDSTooltip>
        )}
        {listState.canChatWithSelection && (
          <MDSPanelResponsiveWrapper minWidth={RESPONSIVE_THRESHOLD}>
            <MDSTooltip
              config={getShortcutTooltipConfig({
                action: "open_mem_agent",
                label: "Chat",
                placement: MDSTooltipPlacement.Top,
              })}
            >
              <MDSButton
                variant={MDSButtonVariant.Chrome}
                onClick={listState.handleChatWithSelection}
              >
                <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Message} />
                Chat
              </MDSButton>
            </MDSTooltip>
          </MDSPanelResponsiveWrapper>
        )}
        {listState.canRestoreFromTrash && (
          <MDSPanelResponsiveWrapper minWidth={RESPONSIVE_THRESHOLD}>
            <MDSButton variant={MDSButtonVariant.Chrome} onClick={listState.handleRestore}>
              Restore from trash
            </MDSButton>
          </MDSPanelResponsiveWrapper>
        )}
        {listState.canMoveNoteToTrash && (
          <MDSPanelResponsiveWrapper minWidth={RESPONSIVE_THRESHOLD}>
            <MDSTooltip
              config={getShortcutTooltipConfig({
                action: "move_notes_to_trash",
                label: "Trash",
                placement: MDSTooltipPlacement.Top,
              })}
            >
              <MDSButton variant={MDSButtonVariant.Chrome} onClick={listState.handleDeleteNote}>
                <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Trash} />
                Trash
              </MDSButton>
            </MDSTooltip>
          </MDSPanelResponsiveWrapper>
        )}
        {listState.canMoveCollectionToTrash && (
          <MDSPanelResponsiveWrapper minWidth={RESPONSIVE_THRESHOLD}>
            <MDSButton
              variant={MDSButtonVariant.Chrome}
              onClick={listState.handleDeleteCollections}
            >
              <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Trash} />
              Delete collection
            </MDSButton>
          </MDSPanelResponsiveWrapper>
        )}
        {/* Wide panel: show only the base more actions */}
        {baseMoreActions.length > 0 && (
          <MDSPanelResponsiveWrapper minWidth={RESPONSIVE_THRESHOLD}>
            <MDSTooltip config={{ label: "More", placement: MDSTooltipPlacement.Top }}>
              <MDSActionMenuDropdown contentList={baseMoreActions} placement="bottom right">
                <MDSIconButton
                  slot={MDSMenuDropdownSlotName.Trigger}
                  iconKind={MDSIconKind.More}
                  size={MDSIconButtonSize.Medium}
                />
              </MDSActionMenuDropdown>
            </MDSTooltip>
          </MDSPanelResponsiveWrapper>
        )}
        {/* Narrow panel: overflow buttons collapse into the more menu */}
        {smallScreenMoreActions.length > 0 && (
          <MDSPanelResponsiveWrapper maxWidth={RESPONSIVE_THRESHOLD}>
            <MDSTooltip config={{ label: "More", placement: MDSTooltipPlacement.Top }}>
              <MDSActionMenuDropdown contentList={smallScreenMoreActions} placement="bottom right">
                <MDSIconButton
                  slot={MDSMenuDropdownSlotName.Trigger}
                  iconKind={MDSIconKind.More}
                  size={MDSIconButtonSize.Medium}
                />
              </MDSActionMenuDropdown>
            </MDSTooltip>
          </MDSPanelResponsiveWrapper>
        )}
      </Box>
    </FloatingActions>
  );
});

const Box = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.surface.chrome};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  display: flex;
  gap: ${({ theme }) => theme.spacing["2"]};
  justify-content: flex-start;
  padding: ${({ theme }) => theme.spacing["3"]};
  font-size: 14px;
`;

const DeselectButton = styled(MDSButton)`
  margin-bottom: ${({ theme }) => theme.spacing["2"]};
`;
